import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Card,
  Container,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Button,
  Breadcrumbs,
} from "@mui/material";
import {
  HomeRounded,
  SchoolOutlined,
  InfoOutlined,
  DarkModeOutlined,
} from "@mui/icons-material";
import { showSuccessToast } from "../../utils/Toast";
import { useColorMode } from "../../theme";

// ── localStorage keys ────────────────────────────────────────────────────────
const ONBOARDING_ENABLED_KEY = "sfm_onboarding_enabled";
const TOOLTIPS_ENABLED_KEY = "sfm_tooltips_enabled";
const ONBOARDING_COMPLETE_KEY = "sfm_onboarding_complete";

function readFlag(key, defaultValue = true) {
  const stored = localStorage.getItem(key);
  if (stored === null) return defaultValue;
  return stored === "true";
}

// ─────────────────────────────────────────────────────────────────────────────
export default function AppPreferences() {
  const [onboardingEnabled, setOnboardingEnabled] = useState(() =>
    readFlag(ONBOARDING_ENABLED_KEY, true)
  );
  const [tooltipsEnabled, setTooltipsEnabled] = useState(() =>
    readFlag(TOOLTIPS_ENABLED_KEY, true)
  );

  const { mode, toggleColorMode } = useColorMode();

  const onboardingComplete =
    localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "v2";

  const handleToggleOnboarding = (e) => {
    const val = e.target.checked;
    setOnboardingEnabled(val);
    localStorage.setItem(ONBOARDING_ENABLED_KEY, String(val));

    if (val && onboardingComplete) {
      // Reset completion so it shows again next page load
      localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    }
    showSuccessToast(
      val ? "Onboarding tutorial enabled — it will show on next page load." : "Onboarding tutorial disabled."
    );
  };

  const handleToggleTooltips = (e) => {
    const val = e.target.checked;
    setTooltipsEnabled(val);
    localStorage.setItem(TOOLTIPS_ENABLED_KEY, String(val));
    showSuccessToast(
      val ? "Navigation tooltips enabled." : "Navigation tooltips hidden."
    );
  };

  const handleResetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    setOnboardingEnabled(true);
    localStorage.setItem(ONBOARDING_ENABLED_KEY, "true");
    showSuccessToast(
      "Onboarding reset — the tutorial will appear on your next page load."
    );
  };

  return (
    <>
      <Helmet>
        <title>App Preferences | SFM</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" />
              <Typography variant="body1" color="textPrimary">
                / Settings / App Preferences
              </Typography>
            </Stack>
          </Breadcrumbs>
        </Stack>

        {/* ── Appearance / Dark Mode ──────────────────────────────────── */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <DarkModeOutlined color="primary" />
            <Typography variant="h6">Appearance</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Switch between light and dark themes. Dark mode is easier on the eyes in low-light
            offices and on shop-floor screens. Your choice is saved on this device.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={mode === "dark"}
                onChange={toggleColorMode}
                color="primary"
              />
            }
            label={mode === "dark" ? "Dark mode" : "Light mode"}
          />
        </Card>

        <Divider sx={{ mb: 3 }} />

        {/* ── Onboarding Tutorial ─────────────────────────────────────── */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <SchoolOutlined color="primary" />
            <Typography variant="h6">Onboarding Tutorial</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The guided walkthrough that introduces every section of SFM to new
            users. It automatically appears once for first-time visitors.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={onboardingEnabled}
                onChange={handleToggleOnboarding}
                color="primary"
              />
            }
            label={onboardingEnabled ? "Enabled" : "Disabled"}
          />

          <Box mt={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetOnboarding}
            >
              Replay Tutorial on Next Page Load
            </Button>
          </Box>

          {onboardingComplete && onboardingEnabled && (
            <Typography variant="caption" color="text.secondary" mt={1}>
              You have already completed the tutorial. Click "Replay" above to
              see it again.
            </Typography>
          )}
        </Card>

        <Divider sx={{ mb: 3 }} />

        {/* ── Navigation Tooltips ─────────────────────────────────────── */}
        <Card sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <InfoOutlined color="primary" />
            <Typography variant="h6">Navigation Tooltips</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Hover-over descriptions on sidebar navigation items that explain
            what each section does (e.g. Kiosk View, Heat Management, Release
            Packages).
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={tooltipsEnabled}
                onChange={handleToggleTooltips}
                color="primary"
              />
            }
            label={tooltipsEnabled ? "Shown on hover" : "Hidden"}
          />
        </Card>
      </Container>
    </>
  );
}
